/**
 * Format a date string or Date object to a readable format
 * @param dateInput - Date string or Date object to format
 * @param locale - Optional locale, defaults to 'en-US'
 * @param options - Optional Intl.DateTimeFormatOptions
 * @returns Formatted date string
 */
export const formatDate = (
  dateInput: string | Date | null | undefined,
  locale = "en-US",
  options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
  },
): string => {
  if (!dateInput) return "N/A";

  const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;

  if (isNaN(date.getTime())) {
    return "Invalid date";
  }

  return new Intl.DateTimeFormat(locale, options).format(date);
};

/**
 * Format a time string or Date object to a readable time format
 * @param timeInput - Time string or Date object to format
 * @param locale - Optional locale, defaults to 'en-US'
 * @param options - Optional Intl.DateTimeFormatOptions
 * @returns Formatted time string
 */
export const formatTime = (
  timeInput: string | Date | null | undefined,
  locale = "en-US",
  options: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  },
): string => {
  if (!timeInput) return "N/A";

  const time = typeof timeInput === "string" ? new Date(timeInput) : timeInput;

  if (isNaN(time.getTime())) {
    return "Invalid time";
  }

  return new Intl.DateTimeFormat(locale, options).format(time);
};

/**
 * Format a datetime string or Date object to readable date and time
 * @param datetimeInput - Datetime string or Date object to format
 * @param locale - Optional locale, defaults to 'en-US'
 * @returns Formatted datetime string
 */
export const formatDateTime = (
  datetimeInput: string | Date | null | undefined,
  locale = "en-US",
): string => {
  if (!datetimeInput) return "N/A";

  const datetime =
    typeof datetimeInput === "string" ? new Date(datetimeInput) : datetimeInput;

  if (isNaN(datetime.getTime())) {
    return "Invalid datetime";
  }

  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  }).format(datetime);
};

/**
 * Format a numeric value as currency
 * @param amount - Amount in cents or as a decimal value
 * @param inCents - Whether the amount is in cents (true) or already in decimal (false)
 * @param currency - Currency code, defaults to 'USD'
 * @param locale - Optional locale, defaults to 'en-US'
 * @returns Formatted currency string
 */
export const formatCurrency = (
  amount: number | null | undefined,
  inCents = true,
  currency = "USD",
  locale = "en-US",
): string => {
  if (amount === null || amount === undefined) return "N/A";

  // Convert cents to dollars if needed
  const value = inCents ? amount / 100 : amount;

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

/**
 * Format a phone number string
 * @param phone - Phone number to format
 * @param defaultFormat - Whether to use US format (XXX) XXX-XXXX
 * @returns Formatted phone number string
 */
export const formatPhone = (
  phone: string | null | undefined,
  defaultFormat = true,
): string => {
  if (!phone) return "N/A";

  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, "");

  // Check for US phone number length and format accordingly
  if (defaultFormat && cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  // International format for longer numbers
  else if (cleaned.length > 10) {
    // Assuming first digit is country code
    return `+${cleaned.slice(0, 1)} ${cleaned.slice(1, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`;
  }

  // Return the cleaned number if it doesn't match standard formats
  return cleaned;
};

/**
 * Format a number with commas for thousands
 * @param number - Number to format
 * @param decimals - Number of decimal places to display
 * @returns Formatted number string
 */
export const formatNumber = (
  number: number | null | undefined,
  decimals = 0,
): string => {
  if (number === null || number === undefined) return "N/A";

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(number);
};

/**
 * Format a percentage value
 * @param value - Percentage value as a decimal (e.g., 0.2564 for 25.64%)
 * @param decimals - Number of decimal places to display
 * @returns Formatted percentage string
 */
export const formatPercent = (
  value: number | null | undefined,
  decimals = 2,
): string => {
  if (value === null || value === undefined) return "N/A";

  return new Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

/**
 * Format file size in bytes to human-readable format
 * @param bytes - Size in bytes
 * @param decimals - Number of decimal places to display
 * @returns Formatted file size string
 */
export const formatFileSize = (bytes: number, decimals = 2): string => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return (
    parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + " " + sizes[i]
  );
};

/**
 * Format a duration in seconds to hours, minutes, seconds
 * @param seconds - Duration in seconds
 * @returns Formatted duration string
 */
export const formatDuration = (seconds: number): string => {
  if (seconds < 0) return "Invalid duration";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  let result = "";

  if (hours > 0) {
    result += `${hours}h `;
  }

  if (minutes > 0 || hours > 0) {
    result += `${minutes}m `;
  }

  result += `${remainingSeconds}s`;

  return result.trim();
};

/**
 * Format a distance in kilometers or miles
 * @param distance - Distance value
 * @param unit - Unit of measurement ('km' or 'mi')
 * @param decimals - Number of decimal places to display
 * @returns Formatted distance string
 */
export const formatDistance = (
  distance: number,
  unit: "km" | "mi" = "km",
  decimals = 1,
): string => {
  if (distance < 0) return "Invalid distance";

  return `${distance.toFixed(decimals)} ${unit}`;
};
